import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { animate, style, transition, trigger, query, stagger } from '@angular/animations';
import { LicenseService } from '@core/services';
import { NotificationService } from '@core/services';
import { AmplitudeService } from '@core/services';

type PlanCta = 'trial' | 'sales';

interface Plan {
  id: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  monthlyOriginalPrice?: number;
  note: string;
  badge?: string;
  includes?: string;
  features: string[];
  cta: PlanCta;
}

@Component({
  selector: 'app-choose-plan',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './choose-plan.component.html',
  styleUrls: ['./choose-plan.component.scss'],
  animations: [
    trigger('fadeUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(24px)' }),
        animate('480ms cubic-bezier(0.16, 1, 0.3, 1)', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('staggerCards', [
      transition('* => *', [
        query('.plan-card', [
          style({ opacity: 0, transform: 'translateY(32px)' }),
          stagger(120, [
            animate('500ms cubic-bezier(0.16, 1, 0.3, 1)', style({ opacity: 1, transform: 'translateY(0)' })),
          ]),
        ], { optional: true }),
      ]),
    ]),
  ],
})
export class ChoosePlanComponent implements OnInit {
  isActivating = false;
  hasUsedTrial = false;
  billingPeriod: 'monthly' | 'annual' = 'annual';

  readonly plans: Plan[] = [
    {
      id: 'free',
      name: 'Free',
      monthlyPrice: 0,
      annualPrice: 0,
      note: '',
      features: [
        '10 meetings/month (max 30 mins/meeting), refreshes every month',
        '1 Question template - map attendee responses in realtime',
        'Get live answers from 1 PDF + 1 website',
        'Meeting summary and download transcription',
      ],
      cta: 'trial',
    },
    {
      id: 'companion',
      name: 'Companion',
      monthlyPrice: 20,
      annualPrice: 10,
      monthlyOriginalPrice: 25,
      note: '',
      badge: 'Most Popular',
      includes: 'Everything in Free plus',
      features: [
        '30 meetings/month',
        'Unlimited question templates',
        '3 PDFs + 3 websites',
        'CRM sync',
        'Email support',
      ],
      cta: 'sales',
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      monthlyPrice: 49,
      annualPrice: 25,
      monthlyOriginalPrice: 69,
      note: 'Minimum 10 seats',
      includes: 'Everything in Companion plus',
      features: [
        'Unlimited meetings',
        'Native desktop app - Botless, invisible',
        'Bot option also available - Google Meet, Zoom',
        'Advanced CRM integration - Map each question to a CRM field',
        'Custom requirements',
        'Dedicated onboarding & priority support',
      ],
      cta: 'sales',
    },
  ];

  constructor(
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _licenseService: LicenseService,
    private readonly _notificationService: NotificationService,
    private readonly _amplitudeService: AmplitudeService,
  ) {}

  ngOnInit(): void {
    this.hasUsedTrial = this._licenseService.hasUsedFreeTrial;
    this._amplitudeService.trackEvent('choose_plan_viewed', {});
  }

  setBillingPeriod(period: 'monthly' | 'annual'): void {
    this.billingPeriod = period;
  }

  getPrice(plan: Plan): number {
    return this.billingPeriod === 'annual' ? plan.annualPrice : plan.monthlyPrice;
  }

  onPlanCta(plan: Plan): void {
    if (plan.cta === 'trial') {
      this.startFreeTrial();
    } else {
      this.contactSales();
    }
  }

  async startFreeTrial(): Promise<void> {
    if (this.isActivating || this.hasUsedTrial) return;

    this.isActivating = true;
    try {
      const license = await this._licenseService.activateFreeTrial();
      this._amplitudeService.trackEvent('free_trial_activated', {
        license_id: license.id,
        end_at: license.end_at,
      });
      this._notificationService.notify('Your Free plan is now active! Welcome to convverse.');

      // Prompt the user to head to Companion once they reach the dashboard.
      localStorage.setItem('convverse_show_companion_prompt', 'true');

      const fromOnboarding = this._route.snapshot.queryParams['fromOnboarding'];
      if (fromOnboarding === 'true') {
        this._router.navigate(['/onboarding'], { queryParams: { showComplete: 'true' } });
      } else {
        this._router.navigate(['/dashboard']);
      }
    } catch (err: any) {
      console.error('[ChoosePlan] activateFreeTrial error:', err);
      if (err?.message === 'trial_already_used') {
        this.hasUsedTrial = true;
        this._notificationService.notify('You are already on the Free plan.');
      } else {
        this._notificationService.notifyError('Failed to activate plan. Please try again.');
      }
    } finally {
      this.isActivating = false;
    }
  }

  contactSales(): void {
    this._amplitudeService.trackEvent('contact_sales_clicked', { source: 'choose_plan' });
    const fromOnboarding = this._route.snapshot.queryParams['fromOnboarding'];
    if (fromOnboarding === 'true') {
      this._router.navigate(['/onboarding'], { queryParams: { showComplete: 'true' } });
    }
    window.open('https://convverse.ai/contact-us', '_blank', 'noopener,noreferrer');
  }
}
